/** "Listem" ekranı: kullanıcının kaydettiği içerikler tek yüzeyde. */

import { type ReactNode, useEffect, useState } from 'react';
import { api, type Item, type Library } from '../api.ts';
import { ItemCard } from '../components/ItemCard.tsx';
import { ErrorNotice, Loading } from '../components/States.tsx';
import { isAudioItem } from '../mediaKind.ts';

function isMusicLibrary(library: Library | undefined): boolean {
  if (library === undefined) return false;
  const lower = library.name.toLowerCase();
  return (
    library.kind === 'music' ||
    lower.includes('müzik') ||
    lower.includes('muzik') ||
    lower.includes('music')
  );
}

export function WatchlistScreen({
  libraries,
  onWatch,
  onOpenItem,
  onExplore,
}: {
  libraries: Library[] | null;
  onWatch: (itemId: string) => void;
  onOpenItem: (itemId: string) => void;
  onExplore: () => void;
}): ReactNode {
  const [items, setItems] = useState<Item[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    let cancelled = false;
    void api
      .catalogPreferences()
      .then((preferences) => {
        if (!cancelled) setLayout(preferences.watchlistLayout);
      })
      .catch(() => {
        // Tercih okunamazsa güvenli varsayılan ızgara kalır.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // `reloadKey` yalnız aynı isteği yeniden denemek için.
    void reloadKey;
    let cancelled = false;
    setItems(null);
    setError(null);
    void api
      .watchlist()
      .then((result) => {
        if (!cancelled) setItems(result.items);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Listem okunamadı.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (error !== null) {
    return <ErrorNotice message={error} onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  if (items === null) return <Loading label="Listem yükleniyor…" />;

  if (items.length === 0) {
    return (
      <div className="watchlist-screen">
        <header className="watchlist-screen__header">
          <h1 className="watchlist-screen__title">Listem</h1>
        </header>
        <div className="search-screen__empty search-screen__empty--page">
          <h2>Listen henüz boş</h2>
          <p>Beğendiğin film, dizi veya müzikleri listeme ekleyerek burada topla.</p>
          <button type="button" className="button button--primary" onClick={onExplore}>
            İçerik keşfet
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="watchlist-screen">
      <header className="watchlist-screen__header">
        <div>
          <h1 className="watchlist-screen__title">Listem</h1>
          <p className="watchlist-screen__count">{items.length} içerik</p>
        </div>
      </header>

      <div className={`grid view-${layout} view-md`}>
        {items.map((item) => {
          const library = libraries?.find((entry) => entry.id === item.libraryId);
          const isMusic = library === undefined ? isAudioItem(item) : isMusicLibrary(library);
          const isPlayable = item.kind === 'movie' || item.kind === 'episode';
          return (
            <ItemCard
              key={item.id}
              item={item}
              layout={isMusic ? 'square' : 'portrait'}
              mediaKind={isMusic ? 'music' : 'video'}
              href={isPlayable ? `#/izle/${item.id}` : `#/oge/${item.id}`}
              onClick={isPlayable ? () => onWatch(item.id) : () => onOpenItem(item.id)}
            />
          );
        })}
      </div>
    </div>
  );
}
